/**
 * Onboarding step config. Configurable per flow; steps can be omitted by filtering this array.
 * No magic numbers: step count = steps.length, indices 0..steps.length-1.
 */

export interface OnboardingStepConfig {
  /** Paths that match this step (for sidebar highlight). Empty = no highlight (e.g. mode switch). */
  paths: readonly string[];
  titleKey: string;
  descriptionKey: string;
}

/** User flow: Dashboard, Folders, Devices, Policies, Access logs, Download */
export const ONBOARDING_USER_STEPS: readonly OnboardingStepConfig[] = [
  { paths: ['/'], titleKey: 'step1.title', descriptionKey: 'step1.description' },
  { paths: ['/folders'], titleKey: 'step2.title', descriptionKey: 'step2.description' },
  { paths: ['/devices'], titleKey: 'step3.title', descriptionKey: 'step3.description' },
  { paths: ['/policies', '/administration/policy'], titleKey: 'step4.title', descriptionKey: 'step4.description' },
  { paths: ['/downloads'], titleKey: 'step6.title', descriptionKey: 'step6.description' },
];

/** Admin flow: starts with User/Admin mode switch at bottom of sidebar, then org-wide settings. */
export const ONBOARDING_ADMIN_STEPS: readonly OnboardingStepConfig[] = [
  { paths: [], titleKey: 'adminStep13.title', descriptionKey: 'adminStep13.description' }, // Mode switch at bottom – first step
  { paths: ['/assets'], titleKey: 'adminStep1.title', descriptionKey: 'adminStep1.description' },
  { paths: ['/folders'], titleKey: 'adminStep2.title', descriptionKey: 'adminStep2.description' },
  { paths: ['/users'], titleKey: 'adminStep3.title', descriptionKey: 'adminStep3.description' },
  { paths: ['/devices'], titleKey: 'adminStep4.title', descriptionKey: 'adminStep4.description' },
  { paths: ['/email-templates'], titleKey: 'adminStep5.title', descriptionKey: 'adminStep5.description' },
  { paths: ['/administration/watermarks'], titleKey: 'adminStep6.title', descriptionKey: 'adminStep6.description' },
  { paths: ['/administration/policy'], titleKey: 'adminStep7.title', descriptionKey: 'adminStep7.description' },
  { paths: ['/administration/passwords'], titleKey: 'adminStep8.title', descriptionKey: 'adminStep8.description' },
  { paths: ['/administration/notifications'], titleKey: 'adminStep9.title', descriptionKey: 'adminStep9.description' },
  { paths: ['/administration/licenses'], titleKey: 'adminStep10.title', descriptionKey: 'adminStep10.description' },
  { paths: ['/administration/asset-protection'], titleKey: 'adminStep11.title', descriptionKey: 'adminStep11.description' },
  { paths: ['/clients'], titleKey: 'adminStep12.title', descriptionKey: 'adminStep12.description' },
];

/** Reader flow: reduced steps for reader users (Assets, Notifications, Download only). */
export const ONBOARDING_READER_STEPS: readonly OnboardingStepConfig[] = [
  { paths: ['/assets'], titleKey: 'readerStep1.title', descriptionKey: 'readerStep1.description' },
  { paths: ['/notifications'], titleKey: 'readerStep2.title', descriptionKey: 'readerStep2.description' },
  { paths: ['/downloads'], titleKey: 'readerStep3.title', descriptionKey: 'readerStep3.description' },
];

export type OnboardingFlowType = 'user' | 'admin' | 'reader';

export function getStepsForFlow(flow: OnboardingFlowType): readonly OnboardingStepConfig[] {
  if (flow === 'admin') return ONBOARDING_ADMIN_STEPS;
  if (flow === 'reader') return ONBOARDING_READER_STEPS;
  return ONBOARDING_USER_STEPS;
}

/** Paths per step for a flow (for sidebar highlight). Index = step index. */
export function getStepPathsForFlow(flow: OnboardingFlowType): readonly (readonly string[])[] {
  return getStepsForFlow(flow).map((s) => s.paths);
}
